import { ChangeEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import Box from '@carrier-io/air-react/Box';
import Button from '@carrier-io/air-react/Button';
import ModalContent from '@carrier-io/air-react/ModalContent';
import ModalActions from '@carrier-io/air-react/ModalActions';
import TextField from '@carrier-io/air-react/TextField';
import Select from '@carrier-io/air-react/Select';
import { Modal, ModalTitle } from '@carrier-io/air-react';
import Stack from '@carrier-io/air-react/Stack';
import IconButton from '@carrier-io/air-react/IconButton';
import InputLabel from '@carrier-io/air-react/InputLabel';
import Input from '@carrier-io/air-react/Input';
import FormControl from '@carrier-io/air-react/FormControl';
import FormHelperText from '@carrier-io/air-react/FormHelperText';
import InputAdornment from '@carrier-io/air-react/InputAdornment';
import EmailIcon from 'components/icons/email-icon';
import { ConfirmPopup } from './ConfirmPopup';
import {
  DeviceTypeDesc,
  NewProjectSchema,
  ProjectOnboardingStatus,
  AHS,
  NZM,
  DataProducerProject,
  UpdatedProjectSchema,
} from 'store/models/Project';
import { Vendor } from 'store/models/models';
import { NotificationBgColor } from 'store/models/Notification';
import { useNotification } from 'context/NotificationContext';
import { TextFieldColor } from 'constants/componentEnums';
import { isEmailValid } from '@carrier-io/air-react/patterns/SignIn/utils/validator';
import { isIdValid, validateEmail, validateFirstName, validateLastName, validateName } from 'utils/validators';
import { CREATE_PROJECT } from 'store/graphql/mutations/CREATE_PROJECT';
import { UPDATE_PROJECT } from 'store/graphql/mutations/UPDATE_PROJECT';
import { GET_DEVICE_TYPES } from 'store/graphql/queries/GET_DEVICE_TYPES';
import { transformDeviceTypes } from 'utils/transformDeviceTypes';
import styles from '../../styles/Popup.module.css';

export type ProjectPopupProps = {
  opened: boolean;
  onCancel: () => void;
  vendorData?: Vendor;
  handleProjectAdded?: () => void;
  editableProject: DataProducerProject | null;
  isEditing?: boolean;
};

export const ProjectPopup = ({
  vendorData,
  opened,
  onCancel,
  handleProjectAdded,
  isEditing,
  editableProject,
}: ProjectPopupProps) => {
  const { vendorId } = useParams();
  const { setIsNotificationOpened, setTextContent, setNotificationBgColor } = useNotification();
  const [confirmPopupOpened, setConfirmPopupOpened] = useState(false);

  const [projectName, setProjectName] = useState('');
  const [projectNameError, setProjectNameError] = useState('');
  const [isProjectNameError, setIsProjectNameError] = useState(false);

  const [leadEmail, setLeadEmail] = useState('');
  const [leadEmailError, setLeadEmailError] = useState('');
  const [isLeadEmailError, setIsLeadEmailError] = useState(false);

  const [projectType, setProjectType] = useState<AHS | NZM>(AHS.FCU);
  const [projectTypeError, setProjectTypeError] = useState('');
  const [isProjectTypeError, setIsProjectTypeError] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [isFirstNameError, setIsFirstNameError] = useState(false);

  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [isLastNameError, setIsLastNameError] = useState(false);

  const [deviceTypes, setDeviceTypes] = useState<typeof DeviceTypeDesc | null>(null);

  const [createProject, { loading: creatingLoading }] = useMutation(CREATE_PROJECT);
  const [updateProject, { loading: updatingLoading }] = useMutation(UPDATE_PROJECT);
  const { data, loading } = useQuery(GET_DEVICE_TYPES, {
    fetchPolicy: 'cache-first',
  });
  const isProjectNameValid = (projectName: string) => {
    const { isValid, errorMessage } = validateName(projectName);

    setIsProjectNameError(!isValid);
    setProjectNameError(errorMessage);

    return isValid;
  };

  const isLeadEmailValid = (leadEmail: string) => {
    const { isValid, errorMessage } = validateEmail(leadEmail);

    setIsLeadEmailError(!isValid);
    setLeadEmailError(errorMessage);

    return isValid;
  };

  const validateProjectType = () => {
    setProjectTypeError('');
    let isError = false;

    const isProjectTypeValid = Object.keys(DeviceTypeDesc).includes(projectType);

    if (projectType.length < 1) {
      setProjectTypeError('Project type is required field');
      isError = true;
    }

    if (!isProjectTypeValid) {
      setProjectTypeError('Project type is invalid');
      isError = true;
    }
    setIsProjectTypeError(isError);

    return isError;
  };

  const isFirstNameValid = (firstName: string) => {
    const { isValid, errorMessage } = validateFirstName(firstName);

    setIsFirstNameError(!isValid);
    setFirstNameError(errorMessage);

    return isValid;
  };

  const isLastNameValid = (lastName: string) => {
    const { isValid, errorMessage } = validateLastName(lastName);

    setIsLastNameError(!isValid);
    setLastNameError(errorMessage);

    return isValid;
  };

  const resetFields = () => {
    setIsProjectNameError(false);
    setProjectNameError('');
    setProjectName('');

    setIsFirstNameError(false);
    setFirstNameError('');
    setFirstName('');

    setIsLastNameError(false);
    setLastNameError('');
    setLastName('');

    setIsLeadEmailError(false);
    setLeadEmailError('');
    setLeadEmail('');

    setIsProjectTypeError(false);
    setProjectTypeError('');
    setProjectType(AHS.FCU);
  };

  const closeConfirmPopup = () => {
    setConfirmPopupOpened(false);
  };

  const openConfirmPopup = () => {
    setConfirmPopupOpened(true);
  };

  const handleClose = () => {
    resetFields();
    onCancel();
    closeConfirmPopup();
  };

  const isValidNewProject = ({
    project: { name, vendorProjectOwnerEmail, firstName, lastName },
    vendorId,
  }: NewProjectSchema) => {
    const { email = '' } = vendorData || {};
    const { isValid } = isEmailValid(email);

    return (
      isProjectNameValid(name) &&
      isFirstNameValid(firstName) &&
      isLastNameValid(lastName) &&
      isLeadEmailValid(vendorProjectOwnerEmail) &&
      !validateProjectType() &&
      isValid &&
      isIdValid(vendorId)
    );
  };

  const isValidUpdatedProject = ({
    vendorId,
    projectId,
    project: { firstName, lastName, vendorProjectOwnerEmail },
  }: UpdatedProjectSchema) =>
    isFirstNameValid(firstName ?? '') &&
    isLastNameValid(lastName ?? '') &&
    isLeadEmailValid(vendorProjectOwnerEmail ?? '') &&
    isIdValid(vendorId) &&
    isIdValid(projectId);

  const getUpdatedProject = (
    { vendorId, projectId, project: { firstName, lastName, vendorProjectOwnerEmail } }: UpdatedProjectSchema,
    editableProject: DataProducerProject | null,
  ): UpdatedProjectSchema | null => {
    const isFirstNameUpdated = firstName !== editableProject?.firstName;
    const isLastNameUpdated = lastName !== editableProject?.lastName;
    const isLeadEmailUpdated = vendorProjectOwnerEmail !== editableProject?.vendorProjectOwnerEmail;
    const isNoFieldsUpdated = !isFirstNameUpdated && !isLastNameUpdated && !isLeadEmailUpdated;

    if (isNoFieldsUpdated) {
      return null;
    }

    const projectToUpdate: UpdatedProjectSchema = {
      vendorId,
      projectId,
      project: {
        ...(isFirstNameUpdated ? { firstName } : {}),
        ...(isLastNameUpdated ? { lastName } : {}),
        ...(isLeadEmailUpdated ? { vendorProjectOwnerEmail } : {}),
      },
    };

    return projectToUpdate;
  };

  const handleCreateProject = async () => {
    const newProjectData: NewProjectSchema = {
      vendorId: vendorId ?? '',
      project: {
        firstName,
        lastName,
        vendorProjectOwnerEmail: leadEmail,
        status: ProjectOnboardingStatus.DRAFT,
        type: projectType,
        name: projectName,
      },
    };

    const isValidData = isValidNewProject(newProjectData);

    if (isValidData) {
      try {
        const response = await createProject({
          variables: newProjectData,
        });
        console.log('Create Project Response: ', response.data);
        setTextContent(`${projectName || 'The project'} has been successfully created`);
        setNotificationBgColor(NotificationBgColor.SUCCESS);
        setIsNotificationOpened(true);
        handleProjectAdded?.();
        handleClose();
      } catch (err) {
        if (err instanceof Error && err.message.includes('User already exists with another vendor')) {
          setIsLeadEmailError(true);
          setLeadEmailError('A user with this email already exists with another vendor');
        }

        if (
          err instanceof Error &&
          err.message.includes(`A data producer named ${projectName} is already exist for this vendor`)
        ) {
          setIsProjectNameError(true);
          setProjectNameError('Data Producer Name already exists');
        }

        setTextContent(`There was an issue with your request. Please check the entered data and try again`);
        setNotificationBgColor(NotificationBgColor.ERROR);
        setIsNotificationOpened(true);
        console.error('Create Project Error: ', err);
      }
    }
  };

  const handleUpdateProject = async () => {
    const updatedProjectData: UpdatedProjectSchema = {
      vendorId: vendorId ?? '',
      projectId: editableProject?.id ?? '',
      project: {
        firstName,
        lastName,
        vendorProjectOwnerEmail: leadEmail,
      },
    };

    const isValid = isValidUpdatedProject(updatedProjectData);

    if (isValid) {
      const updatedProject = getUpdatedProject(updatedProjectData, editableProject);

      if (!updatedProject) {
        setTextContent(`No changes have been saved. Please make updates before submitting`);
        setNotificationBgColor(NotificationBgColor.WARNING);
        setIsNotificationOpened(true);
        return;
      }

      try {
        const response = await updateProject({
          variables: updatedProject,
        });
        console.log('Update Project Response: ', response.data);
        setTextContent(`${projectName || 'The project'} has been successfully updated`);
        setNotificationBgColor(NotificationBgColor.SUCCESS);
        setIsNotificationOpened(true);
        handleProjectAdded?.();
        handleClose();
      } catch (err) {
        if (err instanceof Error && err.message.includes(`User already exists with another vendor`)) {
          setIsLeadEmailError(true);
          setLeadEmailError('A user with this email already exists with another vendor');
        }

        setTextContent(`There was an issue with your request. Please check the entered data and try again`);
        setNotificationBgColor(NotificationBgColor.ERROR);
        setIsNotificationOpened(true);
        console.error('Update Project Error: ', err);
      }
    }
  };

  const handleSubmit = () => {
    if (isEditing) {
      handleUpdateProject();
    } else {
      handleCreateProject();
    }
  };

  const handleFirstNameChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.target.value.trim() === '') {
      setFirstName('');

      return;
    }

    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.target.value.trim() === '') {
      setLastName('');

      return;
    }

    setLastName(event.target.value);
  };

  const handleLeadEmailChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.target.value.trim() === '') {
      setLeadEmail('');

      return;
    }

    setLeadEmail(event.target.value);
  };

  const handleProjectNameChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.target.value.trim() === '') {
      setProjectName('');

      return;
    }

    setProjectName(event.target.value);
  };

  useEffect(() => {
    if (!loading && data) {
      const { getDeviceTypes } = data;
      const transformedDeviceTypes = transformDeviceTypes(getDeviceTypes);

      setDeviceTypes(transformedDeviceTypes as typeof DeviceTypeDesc);
    }
  }, [data]);

  useEffect(() => {
    if (isEditing && editableProject) {
      const { type, name, vendorProjectOwnerEmail, firstName, lastName } = editableProject;

      setProjectType(type);
      setProjectName(name);
      setLastName(lastName);
      setFirstName(firstName);
      setLeadEmail(vendorProjectOwnerEmail);
    }
  }, [isEditing, opened]);

  return (
    <Modal open={opened} fullWidth maxWidth="sm" scroll="body">
      <Box display="flex" flexDirection="column" width="100%">
        <ModalTitle
          data-testid="project-modal-title"
          maxWidth="md"
          className={styles.popupTitle}
          clearable
          onClose={openConfirmPopup}
        >
          {isEditing ? 'Edit Data Producer' : 'Add a Data Producer'}
        </ModalTitle>
        <ModalContent>
          <Box display="flex" flexDirection="column" width="100%">
            <Stack direction="column" spacing={1}>
              <FormControl
                sx={{
                  m: 1,
                  maxWidth: 580,
                  width: '100%',
                  margin: 0,
                }}
                variant="filled"
              >
                <InputLabel className={styles.dataProducerType} required id="bases" sx={{}}>
                  Select a Data Producer type...
                </InputLabel>
                <Select
                  data-testid="project-type-form"
                  required={true}
                  disabled={isEditing}
                  hideBackgroundColor
                  input={<Input hideBackgroundColor showBorder sx={{ minWidth: 150, width: '100%' }} />}
                  label={projectTypeError}
                  onChange={(event) => setProjectType(event.target.value as AHS | NZM)}
                  labelId="bases"
                  native
                  showBorder
                  error={isProjectTypeError}
                  sx={{
                    '& .MuiNativeSelect-select:focus': {
                      backgroundColor: 'transparent',
                    },
                    marginRight: 5,
                    minWidth: 200,
                    width: '100%',
                  }}
                  value={projectType}
                >
                  {deviceTypes &&
                    !!Object.keys(deviceTypes).length &&
                    Object.keys(deviceTypes).map((dt) => (
                      <option key={dt} value={dt}>
                        {deviceTypes[dt as AHS /* | NZM */]}
                      </option>
                    ))}
                </Select>
                <FormHelperText />
              </FormControl>
              <TextField
                data-testid="project-name-form"
                className={styles.nameField}
                required={true}
                disabled={isEditing}
                label="Data Producer Name"
                type="text"
                color={TextFieldColor.Primary}
                showBorder
                onChange={handleProjectNameChange}
                onBlur={() => isProjectNameValid(projectName)}
                helperText={projectNameError}
                hideBackgroundColor
                error={isProjectNameError}
                value={projectName}
              />
              <TextField
                data-testid="project-email-form"
                className={styles.emailField}
                required={true}
                label="Email of Project Owner"
                type="text"
                color={TextFieldColor.Primary}
                showBorder
                onChange={handleLeadEmailChange}
                onBlur={() => isLeadEmailValid(leadEmail)}
                value={leadEmail}
                helperText={leadEmailError}
                hideBackgroundColor
                error={isLeadEmailError}
                size="large"
                inputSetting={{
                  endAdornment: (
                    <InputAdornment className={styles.emailIcon} disableTypography position="start" variant="standard">
                      <IconButton>
                        <EmailIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box display="flex" flexDirection="row" width="100%" justifyContent="space-between" gap={1}>
                <TextField
                  data-testid="project-first-name-form"
                  className={styles.nameField}
                  required={true}
                  label="First Name"
                  type="text"
                  color={TextFieldColor.Primary}
                  showBorder
                  onChange={handleFirstNameChange}
                  onBlur={() => isFirstNameValid(firstName)}
                  helperText={firstNameError}
                  hideBackgroundColor
                  error={isFirstNameError}
                  value={firstName}
                />
                <TextField
                  data-testid="project-last-name-form"
                  className={styles.nameField}
                  required={true}
                  label="Last Name"
                  type="text"
                  color={TextFieldColor.Primary}
                  showBorder
                  onChange={handleLastNameChange}
                  onBlur={() => isLastNameValid(lastName)}
                  helperText={lastNameError}
                  hideBackgroundColor
                  error={isLastNameError}
                  value={lastName}
                />
              </Box>
            </Stack>
          </Box>
        </ModalContent>
        <ModalActions className={styles.modalActions}>
          <Button data-testid="project-cancel-form" variant="text" color="secondary" onClick={openConfirmPopup}>
            Cancel
          </Button>
          <Button
            data-testid="project-submit-form"
            disabled={creatingLoading || updatingLoading}
            variant="outlined"
            color="primary"
            onClick={handleSubmit}
          >
            Save to Vendor
          </Button>
        </ModalActions>
      </Box>
      <ConfirmPopup onCancel={closeConfirmPopup} onConfirm={handleClose} opened={confirmPopupOpened} />
    </Modal>
  );
};
